import type { RoomPhase } from '@cube-rush/shared';
import { useEffect, useReducer } from 'preact/hooks';
import { useApp } from '../../app/context';
import { ru } from '../../i18n/ru';
import { PAD_START_ATTR } from '../../input/focusNav';
import { formatTimeLeft } from '../../util/format';
import { Button } from '../components/Button';
import { MemberCard } from '../room/MemberCard';
import { RaceCard } from '../room/RaceCard';
import { VoiceButton } from '../room/VoiceButton';
import './RoomScreen.css';

/** A gamepad's focus starts at «Я на старте!» (or «Смотреть гонку»). */
const padStart = { [PAD_START_ATTR]: '' };

/** The hub: who is here, voice chat, "ready", the host's start button and all menus. */
export function RoomScreen() {
  const app = useApp();
  const room = app.room.room.value;
  const members = app.room.members.value;
  const me = app.room.me.value;
  const isHost = app.room.isHost.value;
  const myId = app.session.playerId.value;
  const online = app.connection.status.value === 'online';
  const speaking = app.voice.speaking.value;
  const progress = app.race.progress.value;
  const levelCount = app.race.levelCount.value;
  const current = app.race.current.value;
  const results = app.race.results.value;
  const installMode = app.install.mode.value;

  const phase: RoomPhase = room?.phase ?? 'lobby';
  // The local player first, everybody else in the order they joined.
  const ordered = [...members].sort((a, b) => Number(b.id === myId) - Number(a.id === myId));
  const ready = me?.ready ?? false;
  // Between races (lobby, or results of the last race) players get ready / the host starts.
  const betweenRaces = room !== null && (phase === 'lobby' || phase === 'results');
  const watching = current?.role === 'spectator';
  // The running race (countdown, race) is what it is; between races the room's next one.
  const runningRace = phase === 'countdown' || phase === 'race' ? (room?.race ?? null) : null;
  const raceSettings = runningRace?.settings ?? room?.settings;
  const endAt = runningRace?.endAt ?? null;

  return (
    <main class="screen room">
      <header class="room__header">
        <button type="button" class="room__title" onClick={() => app.ui.openDialog('rooms')}>
          <span aria-hidden="true">🏠</span>
          <span class="room__title-text">{room?.name ?? ru.room.defaultTitle}</span>
          {room && (
            <span class="room__title-count">
              {ru.rooms.members(members.length, room.maxMembers)}
            </span>
          )}
          <span class="room__title-caret" aria-hidden="true">
            ▾
          </span>
        </button>
        <Button
          variant="ghost"
          class="btn--square"
          icon="⚙️"
          aria-label={ru.room.settings}
          onClick={() => app.ui.openDialog('settings')}
        />
      </header>

      {/* Until the server places the player in a room there is no phase to tell about. */}
      {room && (
        <PhaseBanner
          phase={phase}
          preparing={room.preparing}
          endAt={endAt}
          readyCount={members.filter((m) => m.ready).length}
          total={members.length}
          isHost={isHost}
        />
      )}

      <section class="room__members" aria-label={ru.room.membersLabel}>
        {room ? (
          <ul class="member-list">
            {ordered.map((member) => (
              <MemberCard
                key={member.id}
                member={member}
                phase={phase}
                isMe={member.id === myId}
                isHost={member.id === room.hostId}
                speaking={speaking.has(member.id)}
                progress={progress.find((entry) => entry.id === member.id)}
                levelCount={levelCount}
                timed={endAt !== null}
              />
            ))}
          </ul>
        ) : (
          <p class="room__empty" role="status">
            {app.connection.stuck.value ? ru.room.noServer : ru.room.noRoom}
          </p>
        )}
      </section>

      <aside class="room__side">
        <VoiceButton />
        {raceSettings && (
          <RaceCard
            settings={raceSettings}
            editable={isHost && betweenRaces}
            onOpen={() => app.ui.openDialog('race')}
          />
        )}
        <div class="room__actions" {...padStart}>
          {watching ? (
            <Button variant="blue" size="big" icon="👀" onClick={() => app.watchRace()}>
              {ru.room.watch}
            </Button>
          ) : (
            <Button
              variant={ready ? 'green' : 'yellow'}
              size="big"
              aria-pressed={ready}
              disabled={!online || !betweenRaces}
              onClick={() => app.setReady(!ready)}
            >
              {ready ? ru.room.readyActive : ru.room.ready}
            </Button>
          )}
          {isHost && betweenRaces && (
            <Button variant="pink" size="big" disabled={!online} onClick={() => app.startRace()}>
              {ru.room.start}
            </Button>
          )}
        </div>
        <nav class="room__tools">
          <ToolButton
            icon="👕"
            label={ru.room.character}
            onClick={() => app.ui.openProfileEditor('edit')}
          />
          <ToolButton
            icon="📨"
            label={ru.room.invite}
            onClick={() => app.ui.openDialog('invite')}
          />
          <ToolButton icon="🚪" label={ru.room.rooms} onClick={() => app.ui.openDialog('rooms')} />
          <ToolButton icon="📊" label={ru.stats.open} onClick={() => app.openStats()} />
          {results && !current && (
            <ToolButton icon="🏆" label={ru.room.results} onClick={() => app.showResults()} />
          )}
          {installMode !== 'none' && (
            <ToolButton
              icon="📲"
              label={ru.room.install}
              onClick={() => void app.promptInstall()}
            />
          )}
        </nav>
      </aside>
    </main>
  );
}

function PhaseBanner({
  phase,
  preparing,
  endAt,
  readyCount,
  total,
  isHost,
}: {
  phase: RoomPhase;
  /** A start waits for the levels of the room's race to be generated. */
  preparing: boolean;
  /** A race against the clock ends at this server time. */
  endAt: number | null;
  readyCount: number;
  total: number;
  isHost: boolean;
}) {
  const getReady = preparing && (phase === 'lobby' || phase === 'results');
  return (
    <section
      class={`room__phase phase phase--${getReady ? 'preparing' : phase}`}
      aria-live="polite"
    >
      <span class="phase__title">{getReady ? ru.race.preparing : ru.room.phase[phase]}</span>
      {phase === 'lobby' && (
        <span class="phase__sub">
          <strong>{ru.room.readyCount(readyCount, total)}</strong>
          {/* The hint makes way on very short screens; the count stays. */}
          <span class="phase__hint"> · {isHost ? ru.room.hostHint : ru.room.guestHint}</span>
        </span>
      )}
      {phase === 'race' && endAt !== null && <TimeLeft endAt={endAt} />}
    </section>
  );
}

/** «осталось 2:31» of a race against the clock, once a second. */
function TimeLeft({ endAt }: { endAt: number }) {
  const app = useApp();
  const left = Math.max(0, endAt - app.net.serverNow());
  const [, tick] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (left <= 0) return;
    // Wake up right after the shown seconds change (they are rounded up).
    const timer = setTimeout(() => tick(undefined), (left % 1000 || 1000) + 5);
    return () => clearTimeout(timer);
  });
  return (
    // Readable, but not read out every second. Bright, like the count in the lobby: the
    // banner of a race is green, the dim colour of a hint fades into it.
    <span class="phase__sub" aria-live="off">
      <strong>{ru.race.timeLeft(formatTimeLeft(left))}</strong>
    </span>
  );
}

function ToolButton({ icon, label, onClick }: { icon: string; label: string; onClick(): void }) {
  return (
    <button type="button" class="tool" onClick={onClick}>
      <span class="tool__icon" aria-hidden="true">
        {icon}
      </span>
      <span class="tool__label">{label}</span>
    </button>
  );
}
